import { type LineSymbol, resolveLineSymbol } from '@ikegps/core-api';
import { T, Translate } from '../../i18n';
import { getSpanLineColor } from './map.line/map.line';
import type { PoleAccuracy } from './map.point/map.point';

/**
 * A legend swatch, described independently of how it is rendered. The on-screen `MapModeLegend`
 * (Mithril/CSS) and the print legend (pdfMake) both consume the same descriptor list so the two
 * can never drift in content, color, or order.
 */
export type LegendSwatch =
	| { kind: 'line'; color: string }
	| { kind: 'point'; accuracy: PoleAccuracy; selected?: boolean }
	| { kind: 'collectionLabel' }
	| { kind: 'lineStyle'; symbol: LineSymbol };

export interface LegendItem {
	swatch: LegendSwatch;
	/** i18n key for a fixed label; `label` takes precedence when both are set. */
	text?: T;
	/** Pre-resolved free-text label (used for per-form line-type names). */
	label?: string;
}

export function legendItemLabel(item: LegendItem): string {
	return item.label ?? (item.text != null ? Translate(item.text) : '');
}

/** Dedupe on shape + color + title together, so two same-title forms with a different shape/color both get a row. */
export function lineStyleLegendItems(
	options: ReadonlyArray<{ symbolId: string; title: string; symbolColor: string }>
): LegendItem[] {
	const seen = new Set<string>();
	const items: LegendItem[] = [];
	for (const option of options) {
		const symbol = resolveLineSymbol(option);
		const key = `${option.symbolId}|${option.symbolColor}|${option.title}`;
		if (!symbol || seen.has(key)) {
			continue;
		}
		seen.add(key);
		items.push({ swatch: { kind: 'lineStyle', symbol }, label: option.title });
	}
	return items;
}

export const LEGEND_ITEMS: LegendItem[] = [
	{ swatch: { kind: 'line', color: getSpanLineColor('dropspan') }, text: T.MAPMODE_LEGEND_DROPSPAN },
	{ swatch: { kind: 'line', color: getSpanLineColor('anchor') }, text: T.MAPMODE_LEGEND_ANCHOR },
	{ swatch: { kind: 'line', color: getSpanLineColor('span') }, text: T.MAPMODE_LEGEND_SPAN },
	{ swatch: { kind: 'line', color: getSpanLineColor('unknown') }, text: T.MAPMODE_LEGEND_UNKNOWN },
	{ swatch: { kind: 'collectionLabel' }, text: T.MAPMODE_LEGEND_COLLECTION_LABEL },
	{ swatch: { kind: 'point', accuracy: 'rtk_fixed', selected: true }, text: T.MAPMODE_LEGEND_SELECTED_COLLECTION },
	{ swatch: { kind: 'point', accuracy: 'rtk_fixed' }, text: T.MAPMODE_LEGEND_LOCATION_RTK_FIXED },
	{ swatch: { kind: 'point', accuracy: 'edited' }, text: T.MAPMODE_LEGEND_LOCATION_REVIEWED },
	{ swatch: { kind: 'point', accuracy: 'rtk_float' }, text: T.MAPMODE_LEGEND_LOCATION_RTK_FLOAT },
	{ swatch: { kind: 'point', accuracy: 'low' }, text: T.MAPMODE_LEGEND_LOCATION_LOW_ACCURACY },
];
